import { AfterViewInit, Component, DestroyRef, ElementRef, HostBinding, HostListener, inject, Input, OnDestroy, OnInit, SimpleChange, SimpleChanges, ViewChild } from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { IconComponent } from '@atoms/icon/icon.component';
import { TitleComponent } from '@atoms/title/title.component';
import { LayoutService } from '@services/layout.service';
import { PopoverService } from '@services/popover.service';
import { ScrollbarService } from '@services/scrollbar.service';
import { SsrService } from '@services/ssr.service';

@Component({
  selector: 'app-popover',
  imports: [NgClass, NgStyle, FormsModule, IconComponent, TitleComponent],
  templateUrl: './popover.component.html',
  styleUrl: './popover.component.scss',
})
export class PopoverComponent implements OnInit, AfterViewInit, OnDestroy {
  private el = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private layoutService = inject(LayoutService);
  public popoverService = inject(PopoverService);
  private scrollbarService = inject(ScrollbarService);
  private ssrService = inject(SsrService);
  @Input() id!: string;
  @Input() search: boolean = false;
  @Input() width?: number;
  @Input() padding?: number;
  @Input('mobile-title') mobileTitle?: string;
  @Input('in-box') inBox: boolean = false;
  @Input() open: boolean = false;
  @HostBinding('class.hidden') @Input() hidden: boolean = false;
  @HostBinding('class.high-z-index') @Input('high-z-index') highZIndex: boolean = false;
  public searched: string = '';
  public direction: 'top' | 'bottom' | 'center' = 'bottom';
  public top?: string;
  public bottom?: string;
  public left?: number;
  private modalEl?: HTMLElement;

  @HostListener('document:click', ['$event.target']) clickout(event: HTMLElement): void {
    const parentEl: HTMLElement | null = event.closest('app-popover');
    const buttonEl = event.closest('[data-popover]') || event.closest('[dynamic-popover]');
    const isDatepicker: boolean = event.classList.contains('mat-calendar') || event.closest('.mat-calendar') !== null;
    const dropdownTeleport: boolean = event.closest('.dropdown-teleport') !== null;
    const popoverButton = (buttonEl?.hasAttribute('data-popover') && buttonEl?.getAttribute('data-popover') === this.id) || (buttonEl?.hasAttribute('dynamic-popover') && buttonEl?.getAttribute('dynamic-popover') === this.id);
    const isInsidePopover = this.el.nativeElement.contains(event);
    this.toggleScroll();
    if (event !== this.el.nativeElement && (!this.open || parentEl === this.el.nativeElement || popoverButton || isDatepicker || isInsidePopover || dropdownTeleport)) return;
    this.toggle(false);
    if (this.id === this.popoverService.activeId()) {
      this.popoverService.activeId.set(null);
      this.layoutService.enableScroll('popover');
    }
    this.searchChange('');
  }

  @HostListener('window:resize') onResize(): void {
    this.toggleScroll();
    if (!this.open) return;
    this.setDirection();
    this.setPosition();
  }

  @HostListener('window:scroll') onScroll(): void {
    if (!this.open) return;
    this.setPosition();
  }

  toggle(active: boolean): void {
    this.open = active;
    if (active) this.el.nativeElement.classList.add('open');
    else this.el.nativeElement.classList.remove('open');
  }

  contentScroll(event: Event): void {
    const scrollTop = (event.target as HTMLElement).scrollTop;
    this.scrollbarService.setValue(this.id, scrollTop);
  }

  toggleScroll(): void {
    if (window.innerWidth < 1000 && this.popoverService.activeId()) this.layoutService.disableScroll('popover');
    else this.layoutService.enableScroll('popover');
  }

  searchChange(search: string): void {
    const items = this.el.nativeElement.querySelectorAll('.popover__list-item');
    this.searched = search;
    items.forEach((item: HTMLElement) => {
      const itemParent: HTMLElement | null = item.closest('app-popover-list-item');
      const text = item.innerText.toLowerCase();
      if (!itemParent) return;
      if (text.includes(search)) {
        itemParent.style.display = 'block';
      } else {
        itemParent.style.display = 'none';
      }
    });
  }

  setDirection(): void {
    if (this.ssrService.isServer()) return;
    const buttonEl: HTMLElement | null = document.querySelector(`[data-popover="${this.id}"]`) || document.querySelector(`[dynamic-popover="${this.id}"]`);
    if (!buttonEl) return;
    const buttonElHeight: number = buttonEl.getBoundingClientRect().height;
    const windowHeight: number = window.innerHeight;
    const positionFromTop: number = buttonEl.getBoundingClientRect().top;
    const positionFromBottom: number = windowHeight - buttonEl.getBoundingClientRect().top - buttonElHeight;
    const popoverHeight: number = this.el.nativeElement.offsetHeight ?? 380;

    if (positionFromBottom > popoverHeight) {
      this.direction = 'bottom';
    } else if (positionFromTop > popoverHeight) {
      this.direction = 'top';
    } else {
      this.direction = 'bottom';
    }
  }

  setPosition(): void {
    if (this.ssrService.isServer()) return;
    const el: HTMLElement = this.el.nativeElement;
    const buttonEl: HTMLElement | null = document.querySelector(`[data-popover="${this.id}"]`) || document.querySelector(`[dynamic-popover="${this.id}"]`);
    if (!buttonEl) return;
    const buttonElTop: number = buttonEl.getBoundingClientRect().top;
    const buttonElLeft: number = buttonEl.getBoundingClientRect().left;
    const buttonElHeight: number = buttonEl.getBoundingClientRect().height;
    const gap: number = 8;
    if (this.direction === 'top') {
      this.top = 'auto';
      this.bottom = window.innerHeight - buttonElTop + gap + 'px';
    } else if (this.direction === 'bottom') {
      this.bottom = 'auto';
      this.top = buttonElTop + buttonElHeight + gap + 'px';
    } else {
      this.top = buttonElTop + el.offsetHeight / 2 - buttonElHeight / 2 + 'px';
      this.bottom = 'auto';
    }
    const halfSize = this.width ? this.width / 2 : 140;
    this.left = buttonElLeft + buttonEl.offsetWidth / 2 - halfSize;
    this.el.nativeElement.style.top = this.top;
    this.el.nativeElement.style.bottom = this.bottom;
    this.el.nativeElement.style.left = this.left + 'px';
  }

  constructor() {
    this.scrollbarService.value$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.setPosition();
    });
  }

  ngOnInit(): void {
    this.popoverService.registerPopover(this);
  }

  ngAfterViewInit(): void {
    if (this.ssrService.isServer()) return;
    this.modalEl = this.el.nativeElement.closest('.modal__content');
    if (this.modalEl) {
      this.modalEl.addEventListener('scroll', () => {
        if (this.open) this.setPosition();
      });
    }
  }

  ngOnDestroy(): void {
    this.modalEl?.removeEventListener('scroll', this.setPosition.bind(this));
    this.popoverService.unregisterPopover(this.id);
    if (this.id === this.popoverService.activeId()) this.popoverService.activeId.set(null);
    this.layoutService.enableScroll('popover');
  }
}
